// src/core/gameRegistry.js
// Single source of truth for per-game metadata, state keys, visuals, resume labels,
// and lazy engine/component loaders. Add a new game here first.
import { designTokens } from '../theme/theme';

const { colors } = designTokens;

export const GAME_REGISTRY = {
  drawing: {
    meta: {
      label: 'Draw & Guess',
      icon: '🎨',
      description: 'One player draws, others guess the word!',
      howToPlay: 'One player is chosen as the artist and given a word. They draw it on the screen while everyone else guesses in the chat. Points are awarded to the artist and the first players to guess correctly!',
      minPlayers: 2, maxPlayers: 12,
      defaultSettings: { maxPlayers: 8, rounds: 3, drawTime: 80 },
    },
    stateKey: null,
    botSupported: false,
    loadEngine: () => import('../games/drawing/DrawingGameEngine').then(m => m.DrawingGameEngine),
    loadComponent: () => import('../games/drawing/DrawingGame').then(m => ({ default: m.DrawingGame })),
  },
  ludo: {
    meta: {
      label: 'Ludo',
      icon: '🎲',
      description: 'Classic race game — get all 4 pieces home first!',
      howToPlay: 'Roll the dice to move your pieces. You must roll a 6 to leave the home base. Race your pieces across the board and get them all into the center to win!',
      minPlayers: 2, maxPlayers: 4,
      defaultSettings: { maxPlayers: 4 },
    },
    stateKey: 'ludoState',
    botSupported: true,
    loadEngine: () => import('../games/ludo/LudoGameEngine').then(m => m.LudoGameEngine),
    loadComponent: () => import('../games/ludo/LudoGame').then(m => ({ default: m.LudoGame })),
  },
  snakeladder: {
    meta: {
      label: 'Snake & Ladder',
      icon: '🐍',
      description: 'Race to 100! Climb ladders, dodge snakes!',
      howToPlay: 'Roll the dice to move your token. Land on a ladder to skip ahead, but be careful—landing on a snake will send you sliding back down! First one to 100 wins.',
      minPlayers: 2, maxPlayers: 12,
      defaultSettings: { maxPlayers: 4 },
    },
    stateKey: 'slState',
    botSupported: true,
    loadEngine: () => import('../games/snakeladder/SnakeLadderGameEngine').then(m => m.SnakeLadderGameEngine),
    loadComponent: () => import('../games/snakeladder/SnakeLadderGame').then(m => ({ default: m.SnakeLadderGame })),
  },
  uno: {
    meta: {
      label: 'Uno',
      icon: '🃏',
      description: 'Match colors and numbers — empty your hand first!',
      howToPlay: 'Match the current card by color or number. Use Action cards like Skip, Reverse, and Draw Two to disrupt opponents. Remember to shout "UNO!" when you have one card left!',
      minPlayers: 2, maxPlayers: 10,
      defaultSettings: { maxPlayers: 6 },
    },
    stateKey: 'unoState',
    botSupported: true,
    resumeLabel: '\u{1F0CF} UNO',
    glow: colors.unoPurple,
    gradient: `linear-gradient(135deg, ${colors.unoRed} 0%, ${colors.violet} 100%)`,
    loadEngine: () => import('../games/uno/UnoGameEngine').then(m => m.UnoGameEngine),
    loadComponent: () => import('../games/uno/UnoGame').then(m => ({ default: m.UnoGame })),
  },
  minigolf: {
    meta: {
      label: 'Mini Golf',
      icon: '⛳',
      description: 'Sink the ball in the fewest strokes!',
      howToPlay: 'Aim your ball and adjust the power to sink it into the hole. The player who completes the course in the fewest total strokes wins!',
      minPlayers: 2, maxPlayers: 10,
      defaultSettings: { maxPlayers: 10 },
    },
    stateKey: 'miniGolfState',
    botSupported: true,
    glow: colors.success,
    gradient: `linear-gradient(135deg, ${colors.success} 0%, ${colors.primary} 100%)`,
    loadEngine: () => import('../games/minigolf/MiniGolfGameEngine').then(m => m.MiniGolfGameEngine),
    loadComponent: () => import('../games/minigolf/MiniGolfGame').then(m => ({ default: m.MiniGolfGame })),
  },
  quiz: {
    meta: {
      label: 'Quiz',
      icon: '🧠',
      description: 'AI-generated trivia — fastest correct answer scores most!',
      howToPlay: 'Choose a topic and the AI generates unique questions. Everyone sees the same question and 4 options. Answer correctly and fast to score points — up to 1000 per question. Most points after all questions wins!',
      minPlayers: 2, maxPlayers: 12,
      defaultSettings: { maxPlayers: 12, questionCount: 8, answerTime: 20, topic: 'general' },
    },
    stateKey: 'quizState',
    botSupported: true,
    glow: colors.primary,
    gradient: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.success} 100%)`,
    loadEngine: () => import('../games/quiz/QuizGameEngine').then(m => m.QuizGameEngine),
    loadComponent: () => import('../games/quiz/QuizGame').then(m => ({ default: m.QuizGame })),
  },
  boggle: {
    meta: {
      label: 'Boggle Party',
      icon: '🔤',
      description: 'Find as many words as you can in the letter grid!',
      howToPlay: 'Race to find words by connecting adjacent letters. Each tile can only be used once per word. Unique words score by length; words found by multiple players score no points.',
      minPlayers: 2, maxPlayers: 12,
      defaultSettings: { maxPlayers: 8, minWordLength: 3, roundDuration: 120 },
    },
    stateKey: 'boggleState',
    botSupported: true,
    loadEngine: () => import('../games/boggle/BoggleGameEngine').then(m => m.BoggleGameEngine),
    loadComponent: () => import('../games/boggle/BoggleGame').then(m => ({ default: m.BoggleGame })),
  },
};

export const GAME_META = Object.fromEntries(
  Object.entries(GAME_REGISTRY).map(([type, definition]) => [type, definition.meta])
);

// Keep the runtime-definition name for existing consumers while maintaining one source.
export const GAME_DEFINITIONS = GAME_REGISTRY;

export const DEFAULT_GAME_TYPE = 'drawing';
export const GAME_TYPES = Object.keys(GAME_REGISTRY);

const resolveType = gameType => (GAME_REGISTRY[gameType] ? gameType : DEFAULT_GAME_TYPE);

// Lazy engine registry: gameType -> () => Promise<EngineClass>
export const GameRegistry = Object.fromEntries(
  GAME_TYPES.map(type => [type, GAME_REGISTRY[type].loadEngine])
);

export const getGameEngine = gameType => GameRegistry[gameType] || null;
export const loadGameEngine = gameType => GAME_REGISTRY[resolveType(gameType)].loadEngine();

export const getGameMeta = gameType => GAME_META[resolveType(gameType)];
export const getGameStateKey = gameType => GAME_REGISTRY[gameType]?.stateKey || null;
export const isBotSupportedGame = gameType => !!GAME_REGISTRY[gameType]?.botSupported;

export const getGameGlow = gameType => (
  designTokens.games.glows[gameType] || GAME_REGISTRY[gameType]?.glow || colors.primary
);

export const getGameGradient = gameType => (
  designTokens.games.gradients[gameType] ||
  GAME_REGISTRY[gameType]?.gradient ||
  designTokens.games.gradients[DEFAULT_GAME_TYPE]
);

export const getResumeLabel = gameType => {
  const meta = GAME_META[gameType];
  if (!meta) return 'Game';
  return GAME_REGISTRY[gameType].resumeLabel || `${meta.icon} ${meta.label}`;
};

// A room's game state exists once the host has initialised it.
export const isGameStateReady = room => {
  if (!room) return false;
  if (room.gameType === 'drawing') return !!room.currentDrawer;
  const key = getGameStateKey(room.gameType);
  return key ? !!room[key] : true;
};
